import { useCallback, useRef } from "react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";

export type ChartSeries = {
  key: string;
  name: string;
  color: string;
};

export type ChartDatum = {
  label: string;
  [seriesKey: string]: string | number;
};

type DistributionBarChartProps = {
  data: ChartDatum[];
  series: ChartSeries[];
  height?: number;
  totalUnit: string;
  formatValue: (value: number) => string;
};

const WIDE_BAR_MAX_COLUMNS = 7;
const WIDE_BAR_SIZE = 52;

type TooltipEntry = { dataKey?: string | number; value?: number };

const ChartTooltip = ({
  active,
  payload,
  label,
  series,
  totalUnit,
  formatValue,
  containerRef,
  coordinate,
  viewBox,
  columns,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string | number;
  series: ChartSeries[];
  totalUnit: string;
  formatValue: (value: number) => string;
  containerRef: React.RefObject<HTMLDivElement | null>;
  coordinate?: { x?: number };
  viewBox?: { width?: number };
  columns: number;
}) => {
  if (!active || !payload?.length) return null;

  const total = payload.reduce((acc, entry) => acc + (Number(entry.value) || 0), 0);

  const containerWidth = containerRef.current?.getBoundingClientRect().width ?? 0;
  const containerLeft = containerRef.current?.getBoundingClientRect().left ?? 0;
  const tooltipLeft = containerLeft + (coordinate?.x ?? 0);
  const offsetFromCentre = (viewBox?.width ?? containerWidth) / Math.max(columns, 1) / 2 + 10;
  const overflows = typeof window !== "undefined" && tooltipLeft + 230 > window.innerWidth;

  return (
    <div
      style={{ transform: overflows ? `translateX(calc(-100% - ${offsetFromCentre}px))` : undefined }}
      className="pointer-events-none min-w-[230px] rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm"
    >
      <p className="text-[11px] font-medium text-gray-500">{label}</p>
      <p className="mb-2 flex items-baseline gap-1.5 text-[15px] font-semibold text-gray-900 tabular-nums">
        {formatValue(total)}
        <span className="text-[11px] font-medium text-gray-500">{totalUnit}</span>
      </p>
      <div className="flex flex-col gap-1">
        {series.map((entry) => {
          const value = Number(payload.find((item) => item.dataKey === entry.key)?.value) || 0;
          const share = total > 0 ? Math.round((value / total) * 100) : 0;

          return (
            <div key={entry.key} className="flex items-center gap-2 text-xs">
              <span
                aria-hidden="true"
                className="inline-block size-2 shrink-0 rounded-[2px]"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-gray-600">{entry.name}</span>
              <span className="ml-auto font-medium text-gray-900 tabular-nums">
                {formatValue(value)} <span className="font-normal text-gray-500">({share}%)</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export function DistributionBarChart({
  data,
  series,
  height = 120,
  totalUnit,
  formatValue,
}: DistributionBarChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const renderTooltip = useCallback(
    (props: object) => (
      <ChartTooltip
        {...props}
        series={series}
        totalUnit={totalUnit}
        formatValue={formatValue}
        containerRef={containerRef}
        columns={data.length}
      />
    ),
    [series, totalUnit, formatValue, data.length],
  );

  return (
    <div ref={containerRef} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} barCategoryGap="14%" margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
          <XAxis dataKey="label" hide />
          <Tooltip
            cursor={{ fill: "rgba(33,19,58,0.06)" }}
            wrapperStyle={{ zIndex: 1000, outline: "none" }}
            allowEscapeViewBox={{ x: true, y: true }}
            content={renderTooltip}
          />
          {series.map((entry, index) => (
            <Bar
              key={entry.key}
              dataKey={entry.key}
              stackId="distribution"
              fill={entry.color}
              barSize={data.length <= WIDE_BAR_MAX_COLUMNS ? WIDE_BAR_SIZE : undefined}
              radius={index === series.length - 1 ? [3, 3, 0, 0] : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
